import { useCallback, useEffect, useRef } from "react";
import { NavigateFunction } from "react-router-dom";
import {
  getReceptionCommonWord,
  getMenuWords,
  getMenuButton,
  getAppSettingsData,
  getPhoneBooksEntity,
  getActivationData,
  saveActivationData,
  savePhoneBooksEntity,
  syncPhonebookAndAppSettingsByDisplayIds,
  clearAppSettingsData,
  saveAppSettingsData,
} from "~/utils/receptionDB";
import type {
  MenuWords,
  MenuWordEntity,
  ReceptionCommonWordEntity,
  SettingMenuButtonEntity,
  PhoneBooksEntity,
  ActivationData,
  ActivationCheckResponse,
  AppSettingsResponse,
} from "~/utils/receptionDB.types";
import { ROUTE } from "~/constants/routes";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import { formatIsoDateTimeByTimeZone } from "~/utils/dateTimeUtils";

// Normalize browser/app locale values to app setting language keys.
/**
 * ブラウザ/アプリのロケール文字列を、受付画面で使用する言語コードに正規化する。
 * @param lang ロケール文字列（例: ja-JP, en-US, vi-VN）
 * @returns サポート済み言語コード（ja / en / vi）。未対応は ja を返す。
 */
export const mapReceptionLanguage = (lang: string): string => {
  if (lang.startsWith("en")) return "en";
  if (lang.startsWith("vi")) return "vi";
  return "ja";
};

/**
 * 受付共通文言エンティティから、指定言語に対応する screenWords を取得する。
 * @param receptionCommonWord 受付共通文言エンティティ
 * @param language 現在の表示言語
 * @returns 指定言語に一致する screenWords。取得できない場合は undefined。
 */
export const getReceptionCommonWordsByLanguage = (
  receptionCommonWord: ReceptionCommonWordEntity | undefined,
  language: string
): ReceptionCommonWordEntity["items"][number]["screenWords"] | undefined => {
  const items = receptionCommonWord?.items;
  if (!items) return undefined;

  const langCode = mapReceptionLanguage(language);
  return items.find((word) => word.languageCode === langCode)?.screenWords;
};

/**
 * 受付共通文言から、指定言語・指定フィールドの画面文言を取得する。
 * @param language 現在の表示言語
 * @param field 取得対象の screenWords フィールド名
 * @returns 文言文字列。取得できない場合は空文字を返す。
 */
export const getScreenReceptionCommonWord = async (
  language: string,
  field: keyof ReceptionCommonWordEntity["items"][number]["screenWords"]
): Promise<string> => {
  try {
    const receptionCommonWord = await getReceptionCommonWord();
    return getReceptionCommonWordsByLanguage(receptionCommonWord, language)?.[field] ?? "";
  } catch (err) {
    console.error("Failed to load reception common word:", err);
    return "";
  }
};

/**
 * menuWords配列から、指定会社・指定言語に対応するメニュー文言を取得する。
 * @param menuWords menuWordsエンティティ配列
 * @param companyId 対象会社ID
 * @param language 現在の表示言語
 * @returns 指定条件に一致する menuWords。取得できない場合は undefined。
 */
export const getMenuWordsByLanguage = (
  menuWords: MenuWordEntity[] | undefined,
  companyId: number,
  language: string
): MenuWords | undefined => {
  const menuWordEntity = menuWords?.find((entity) => entity.id === companyId);
  if (!menuWordEntity) return undefined;

  const langCode = mapReceptionLanguage(language);
  return menuWordEntity.items.find((item) => item.languageCode === langCode)?.menuWords;
};

/**
 * メニュー文言から、指定会社・指定言語・指定フィールドの文言を取得する。
 * @param companyId 対象会社ID
 * @param language 現在の表示言語
 * @param field 取得対象の menuWords フィールド名
 * @returns 指定フィールドに対応する文言値。取得できない場合は undefined。
 */
export const getScreenMenuWords = async <K extends keyof MenuWords>(
  companyId: number,
  language: string,
  field: K
): Promise<MenuWords[K] | undefined> => {
  try {
    const menuWords = await getMenuWords();
    return getMenuWordsByLanguage(menuWords, companyId, language)?.[field];
  } catch (err) {
    console.error("Failed to load menu words by field:", err);
    return undefined;
  }
};

/**
 * 会社IDに対応するメニューボタン設定を取得する。
 * @param companyId 対象会社ID
 * @returns メニューボタン設定。見つからない場合は undefined。
 */
export const getScreenMenuButton = async (
  companyId: number
): Promise<SettingMenuButtonEntity | undefined> => {
  try {
    const menuButtons = await getMenuButton();
    return menuButtons?.find((menuButton) => menuButton.id === companyId);
  } catch (err) {
    console.error("Failed to load menu button by companyId:", err);
    return undefined;
  }
};

const DEFAULT_INACTIVITY_TIMEOUT_SECONDS = 30;

/**
 * 無操作タイムアウトを処理するカスタムフック
 * X秒間操作がない場合（クリック、タッチ、キー入力）→ /reception/loading に遷移する
 * タイムアウト値は設定から自動的に取得します
 *
 * @param navigate - React Router のナビゲート関数
 * @param externalActivitySignal - 外部イベントのシグナル。値が変化したときにタイマーをリセットする
 * @returns stopTimer/startTimer - タイマーの停止・再開関数（API処理前後などに使用）
 *
 * @example
 * // 基本的な使用
 * const navigate = useNavigate();
 * useInactivityTimeout(navigate);
 *
 * // タイマー停止が必要な場合
 * const { stopTimer, startTimer } = useInactivityTimeout(navigate);
 * const handleApiCall = async () => {
 *   stopTimer();
 *   await callApi();
 *   startTimer();
 * };
 */
export const useInactivityTimeout = (
  navigate: NavigateFunction,
  externalActivitySignal?: unknown
): { stopTimer: () => void; startTimer: () => void } => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const timeoutSecondsRef = useRef<number | undefined>(undefined);
  const abortedRef = useRef(false);
  const pausedRef = useRef(false);
  const requestIdRef = useRef(0);

  const scheduleTimer = useCallback(() => {
    if (timeoutSecondsRef.current === undefined || abortedRef.current) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    timerRef.current = setTimeout(() => {
      navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
    }, timeoutSecondsRef.current * 1000);
  }, [navigate]);

  // タイマーを停止する関数。タイムアウトが発生しないようにする。フックが破棄されている場合も安全に呼び出せる。
  const stopTimer = useCallback(() => {
    pausedRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  // タイマーを開始または再開する関数。タイムアウト値が未設定またはフックが破棄されている場合は何もしない。
  const startTimer = useCallback(() => {
    pausedRef.current = false;
    scheduleTimer();
  }, [scheduleTimer]);

  const resetTimer = useCallback(() => {
    if (timeoutSecondsRef.current === undefined || abortedRef.current || pausedRef.current) {
      return;
    }
    scheduleTimer();
  }, [scheduleTimer]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const getTimeout = async () => {
      try {
        const appSettings = await getAppSettingsData();
        return appSettings?.standby?.returnBackgroundTime ?? DEFAULT_INACTIVITY_TIMEOUT_SECONDS;
      } catch (err) {
        console.error("Failed to load standby settings:", err);
        return DEFAULT_INACTIVITY_TIMEOUT_SECONDS;
      }
    };

    const initializeTimer = async () => {
      const timeout = await getTimeout();
      if (abortedRef.current || requestIdRef.current !== requestId) {
        return;
      }
      timeoutSecondsRef.current = timeout;
      resetTimer();
    };

    abortedRef.current = false;
    const events = ["click", "mousemove", "touchstart", "keydown"];
    void initializeTimer();
    events.forEach((e) => window.addEventListener(e, resetTimer));

    return () => {
      abortedRef.current = true;
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [resetTimer]);

  useEffect(() => {
    if (externalActivitySignal === undefined || externalActivitySignal === "") {
      return;
    }
    resetTimer();
  }, [externalActivitySignal, resetTimer]);

  return { stopTimer, startTimer };
};

const isChangedPlanOrOption = (
  activation: ActivationData | undefined,
  phoneBooksData: PhoneBooksEntity
): boolean => {
  if (!activation) {
    return false;
  }

  return (
    activation.plan !== phoneBooksData.plan ||
    activation.isAppointmentEnabled !== phoneBooksData.isAppointmentEnabled ||
    activation.isSelectCompanyEnabled !== phoneBooksData.isSelectCompanyEnabled ||
    activation.isFacialIdentificationEnabled !== phoneBooksData.isFacialIdentificationEnabled
  );
};

const saveLatestAppSettings = async () => {
  await clearAppSettingsData();

  const appSettingsUrl = buildIfaceApiUrl("v1/app-settings");
  const headers = createIfaceHeaders(true);
  const appSettingsResponse = await apiClient.get<AppSettingsResponse>(appSettingsUrl, {
    headers,
  });

  await saveAppSettingsData(appSettingsResponse.data as AppSettingsResponse);
};

/**
 * 電話帳データを IndexedDB に同期し、displayId の差分に応じてメニュー設定（menuWords/deliveryWords/menuButtons/basics）も整合させる。
 * プラン/オプション変更がある場合は最新の app-settings を再取得して保存する。
 */
export const syncPhoneBooksData = async (phoneBooksData: PhoneBooksEntity) => {
  const activation = await getActivationData();
  const isChanged = isChangedPlanOrOption(activation, phoneBooksData);

  if (activation) {
    const nextActivation: ActivationData = {
      ...activation,
      isAppointmentHistoryEnabled: phoneBooksData.isAppointmentHistoryEnabled,
    };

    if (isChanged) {
      nextActivation.plan = phoneBooksData.plan;
      nextActivation.isAppointmentEnabled = phoneBooksData.isAppointmentEnabled;
      nextActivation.isSelectCompanyEnabled = phoneBooksData.isSelectCompanyEnabled;
      nextActivation.isFacialIdentificationEnabled = phoneBooksData.isFacialIdentificationEnabled;
    }

    await saveActivationData(nextActivation);
  }
  if (isChanged) {
    await savePhoneBooksEntity(phoneBooksData);
    await saveLatestAppSettings();
    return;
  }

  const appSettings = await getAppSettingsData();
  if (appSettings === undefined) {
    // app settings が未保存の場合は設定同期が行えないため、電話帳のみ保存する
    await savePhoneBooksEntity(phoneBooksData);
    return;
  }

  // Get displayIds from the current entity stored in IndexedDB (before overwrite)
  const currentEntity = await getPhoneBooksEntity();
  const displayIds = (currentEntity?.groupList ?? [])
    .filter((entity) => entity.parentGroupId === null)
    .map((entity) => entity.displayId);

  // Get newDisplayIds from the incoming phoneBooksData
  const newDisplayIds = phoneBooksData.groupList
    .filter((entity) => entity.parentGroupId === null)
    .map((entity) => entity.displayId);

  const requireDeleteSettingIdList = displayIds.filter((id) => !newDisplayIds.includes(id));
  const requireCreateSettingIdList = newDisplayIds.filter((id) => !displayIds.includes(id));

  await syncPhonebookAndAppSettingsByDisplayIds(
    phoneBooksData,
    requireDeleteSettingIdList,
    requireCreateSettingIdList
  );
};

/**
 * activation/check の結果と IndexedDB の ActivationData を比較し、
 * maxUser / expireDate に差分があれば更新して保存する（両方変わっていれば両方更新）。
 *
 * UI 状態（有効期限表示など）には関与しないため、必要な画面は戻り値を使って個別に反映する。
 *
 * @param checkData activation/check のレスポンス
 * @returns 更新した ActivationData。差分が無い/対象データが無い場合は null。
 */
export const syncActivationDataFromCheck = async (
  checkData: ActivationCheckResponse | undefined
): Promise<ActivationData | null> => {
  const activation = checkData?.activation;
  if (!activation) return null;

  const current = await getActivationData();
  if (!current) return null;

  const nextMaxUser = activation.maxUser;
  const nextExpireDate = activation.expireDate;
  const isMaxUserChanged = nextMaxUser !== null && nextMaxUser !== current.maxUser;
  const isExpireDateChanged = Boolean(nextExpireDate) && nextExpireDate !== current.expireDate;

  if (!isMaxUserChanged && !isExpireDateChanged) return null;

  const updatedData: ActivationData = { ...current };
  if (isMaxUserChanged) {
    updatedData.maxUser = nextMaxUser as number;
  }
  if (isExpireDateChanged) {
    updatedData.expireDate = nextExpireDate;
  }
  await saveActivationData(updatedData);

  return updatedData;
};

/** activation/check から解決した自動更新の予約時刻（timezone 整形済み） */
export interface ResolvedAutoUpdateTimes {
  /** app-settings 自動更新の予約時刻。取得できない場合は null */
  appSettingUpdateDateTime: string | null;
  /** phonebook 自動更新の予約時刻。取得できない場合は null */
  phoneBookStartDateTime: string | null;
}

/**
 * activation/check の応答から app-settings / phonebook の自動更新予約時刻を取り出し、
 * timezone があればその timezone で整形して返す。timezone が無ければ ISO 文字列のまま返す。
 *
 * @param checkData activation/check のレスポンス
 * @param timezone activation のタイムゾーン
 */
export const resolveAutoUpdateTimesFromCheck = (
  checkData: ActivationCheckResponse | undefined | null,
  timezone: string | undefined
): ResolvedAutoUpdateTimes => {
  const format = (value: string | null | undefined): string | null => {
    if (!value) return null;
    return timezone ? formatIsoDateTimeByTimeZone(value, timezone) : value;
  };

  return {
    appSettingUpdateDateTime: format(checkData?.appSettingUpdateDateTime),
    phoneBookStartDateTime: format(checkData?.phoneBookStartDateTime),
  };
};
